// Self-serve phone 4b-2: browser dialer device hook.
//
// Wraps the Twilio Voice JS SDK (@twilio/voice-sdk). Fetches a short-lived
// access token from the voice-token edge function for the current sub_account,
// builds a Twilio.Device, registers it, auto-refreshes the token before expiry,
// and exposes a small call API. Outbound only (incoming.allow=false server side).
//
// makeCall sends { To, SubAccountId, ContactType, ContactId } as connect params,
// which Twilio delivers to the voice-browser-outbound TwiML App webhook.

import { useCallback, useEffect, useRef, useState } from 'react'
// ENTRY-CHUNK-1 — TYPE ONLY. The Twilio Voice SDK is 138.6 KB across eleven
// modules and was the largest single thing in the entry chunk after React and
// session replay. A stranger at the front door cannot make a call: this hook's
// effect returns immediately when there is no sub-account, so for them the SDK
// was 138 KB that could never be used.
//
// The constructor is fetched where the Device is actually built, in an effect
// that already awaits a token, so the wait costs a merchant nothing they were
// not already waiting for.
import type { Device, Call } from '@twilio/voice-sdk'
import { useAuth } from './useAuth'
import { supabase } from '../lib/supabase'
import { messageFromInvokeError } from '../lib/invokeError'

export type CallState = 'idle' | 'connecting' | 'ringing' | 'in_call'

export interface MakeCallOpts {
  contactType?: string | null
  contactId?: string | null
}

async function fetchToken(subAccountId: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('voice-token', {
    body: { sub_account_id: subAccountId },
  })
  const errMsg = (data as { error?: string } | null)?.error ?? (error ? error.message : null)
  if (errMsg) throw new Error(errMsg)
  const token = (data as { token?: string } | null)?.token
  if (!token) throw new Error('Could not get a calling token.')
  return token
}

export function useVoiceDevice() {
  const { currentSubAccountId } = useAuth()
  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)

  const [ready, setReady] = useState(false)
  const [callState, setCallState] = useState<CallState>('idle')
  const [muted, setMuted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Build the Device for the current sub_account; rebuild if it changes.
  useEffect(() => {
    if (!currentSubAccountId) return
    let cancelled = false
    let device: Device | null = null

    void (async () => {
      try {
        const token = await fetchToken(currentSubAccountId)
        if (cancelled) return
        const { Device: TwilioDevice } = await import('@twilio/voice-sdk')
        if (cancelled) return
        device = new TwilioDevice(token, { logLevel: 'error' })
        deviceRef.current = device

        device.on('registered', () => !cancelled && setReady(true))
        device.on('unregistered', () => !cancelled && setReady(false))
        device.on('error', (e: { message?: string }) => {
          if (!cancelled) setError(e?.message ?? 'Calling error')
        })
        // Refresh the token shortly before it expires.
        device.on('tokenWillExpire', () => {
          void (async () => {
            try {
              const next = await fetchToken(currentSubAccountId)
              device?.updateToken(next)
            } catch (e) {
              if (!cancelled) setError(await messageFromInvokeError(e, 'Token refresh failed'))
            }
          })()
        })

        await device.register()
      } catch (e) {
        if (!cancelled) setError(await messageFromInvokeError(e, 'Could not start calling'))
      }
    })()

    return () => {
      cancelled = true
      try {
        callRef.current?.disconnect()
      } catch {
        /* ignore */
      }
      callRef.current = null
      try {
        device?.destroy()
      } catch {
        /* ignore */
      }
      deviceRef.current = null
      setReady(false)
      setCallState('idle')
      setMuted(false)
    }
  }, [currentSubAccountId])

  const requestMicPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      return true
    } catch {
      setError('Microphone access is blocked. Allow the mic in your browser to make calls.')
      return false
    }
  }, [])

  const wireCall = useCallback((call: Call) => {
    callRef.current = call
    setMuted(false)
    call.on('ringing', () => setCallState('ringing'))
    call.on('accept', () => setCallState('in_call'))
    const end = () => {
      setCallState('idle')
      setMuted(false)
      callRef.current = null
    }
    call.on('disconnect', end)
    call.on('cancel', end)
    call.on('reject', end)
    call.on('error', (e: { message?: string }) => {
      setError(e?.message ?? 'Call error')
      end()
    })
  }, [])

  const makeCall = useCallback(
    async (toNumber: string, opts?: MakeCallOpts) => {
      const device = deviceRef.current
      if (!device || !currentSubAccountId) {
        setError('Calling is not ready yet.')
        return
      }
      const okMic = await requestMicPermission()
      if (!okMic) return
      setError(null)
      setCallState('connecting')
      try {
        const call = await device.connect({
          params: {
            To: toNumber,
            SubAccountId: currentSubAccountId,
            ContactType: opts?.contactType ?? '',
            ContactId: opts?.contactId ?? '',
          },
        })
        wireCall(call)
      } catch (e) {
        setError(await messageFromInvokeError(e, 'Could not place the call.'))
        setCallState('idle')
      }
    },
    [currentSubAccountId, requestMicPermission, wireCall],
  )

  const hangUp = useCallback(() => {
    try {
      callRef.current?.disconnect()
    } catch {
      /* ignore */
    }
  }, [])

  const mute = useCallback((next?: boolean) => {
    const call = callRef.current
    if (!call) return
    const value = next ?? !call.isMuted()
    call.mute(value)
    setMuted(value)
  }, [])

  return {
    ready,
    callState,
    muted,
    error,
    makeCall,
    hangUp,
    mute,
    requestMicPermission,
  }
}
